import type { SampleSpecRow } from './index';

export type RangeBounds = '[]' | '()' | '[)' | '(]';

export type AcceptedRange =
  | {
      kind: 'between';
      min: number;
      max: number;
      bounds?: RangeBounds;
      preferred?: [number, number];
    }
  | {
      kind: 'gte' | 'gt';
      min: number;
      preferred?: [number, number];
    }
  | {
      kind: 'lte' | 'lt';
      max: number;
      preferred?: [number, number];
    };

export type SampleSheetColumnMeta = {
  header: string;
  tooltip?: string | null;
  acceptedRange?: AcceptedRange;
  notes?: string | null;
  showRangeHint?: boolean;
  softValidationEnabled?: boolean;
};

const nonEditableNote = 'Derived from submitted samples; always read-only.';
const samplingPointNote = 'Sampling point level field.';
const wwtpNote = 'Visible only for WWTP sampling points (future visibility rules).';
const labServiceNote = 'Hidden when DNA extraction/dilution service is provided (future feature).';

const nanoDropRange: AcceptedRange = {
  kind: 'between',
  min: 1.7,
  max: 2.1,
  bounds: '[]',
  preferred: [1.8, 2.0],
};

const nanoDrop230Range: AcceptedRange = {
  kind: 'between',
  min: 0,
  max: 10,
};

const dilutedConcentrationRange: AcceptedRange = {
  kind: 'between',
  min: 8,
  max: 15,
  bounds: '()',
};

export const sampleSheetColumnMetadata: Record<keyof SampleSpecRow, SampleSheetColumnMeta> = {
  sampleId: {
    header: 'Sample\nID',
    tooltip: 'Database sample identifier (assigned after submission).',
    notes: nonEditableNote,
  },
  sampleNumber: {
    header: 'Sample number',
    tooltip: 'Sequential number assigned to each sample.',
    notes: nonEditableNote,
  },
  bioReplica: {
    header: 'Bio replica',
    tooltip: 'Automatically assigned biological replicate (A, B, C...).',
    notes: nonEditableNote,
  },
  sampleDescription: {
    header: 'Sampling point description',
    tooltip:
      'Sampling point or sample name; must be unique for each sampled microbiome.\n\nColours indicate linked rows:\n• Violet = linked to an existing sampling point.\n• Green = drafting a new sampling point that is shared by multiple rows.\n• Blue = drafting a new sampling point that currently appears on for this row only.',
    notes: samplingPointNote,
  },
  sampleType: {
    header: 'Sample type\n\u00a0',
    tooltip: 'General environment type such as wastewater, soil, or stool.',
    notes: samplingPointNote,
  },
  detailedSampleType: {
    header: 'Detailed\nsample type',
    tooltip: 'Detailed subtype such as urban wastewater, agricultural soil, or human stool.',
    notes: samplingPointNote,
  },
  flowRate: {
    header: 'Flow rate\n(m3/d)',
    tooltip: 'Total flow rate, including operated and bypass flow (m³/d).',
    notes: wwtpNote,
  },
  operatedFlowRate: {
    header: 'Operated flow rate\n(m3/d)',
    notes: wwtpNote,
  },
  suspendedSolids: {
    header: 'Suspended Solids\n(mg/L)',
    notes: wwtpNote,
  },
  bod: {
    header: 'BOD\n(mg/L)',
    notes: 'Always hidden until BOD type selection is available.',
  },
  filteredVolume: {
    header: 'Filtered Volume\n(mL)',
    notes: 'Required for water samples.',
  },
  primElutedDnaVolume: {
    header: 'Eluted DNA volume\n(\u00b5L)',
    notes: labServiceNote,
  },
  primElutedDnaConcentration: {
    header: 'Eluted DNA\nconcentration\n(ng/\u00b5l)',
    tooltip: 'Measured with the NanoDrop One spectrophotometer.',
    notes: labServiceNote,
  },
  primElutedDnaQuality280: {
    header: 'Eluted DNA\nquality 260/280\n(1.8 – 2 \u00b10.1)',
    tooltip: 'Measured with the NanoDrop One spectrophotometer. Should be between 1.7 and 2.1.',
    acceptedRange: nanoDropRange,
    notes: labServiceNote,
  },
  primElutedDnaQuality230: {
    header: 'Eluted DNA\nquality 260/230',
    tooltip: 'Measured with the NanoDrop One spectrophotometer.',
    acceptedRange: nanoDrop230Range,
    showRangeHint: false,
    softValidationEnabled: false,
    notes: labServiceNote,
  },
  primDilutedDnaConcentration: {
    header: 'Diluted DNA\nconcentration\n(must be 10 ng/\u00b5l)',
    tooltip: 'Measured with the NanoDrop One spectrophotometer.',
    acceptedRange: dilutedConcentrationRange,
    notes: labServiceNote,
  },
  primDilutedDnaQuality280: {
    header: 'Diluted DNA\nquality 260/280\n(1.8 – 2 \u00b10.1)',
    tooltip: 'Measured with the NanoDrop One spectrophotometer.',
    acceptedRange: nanoDropRange,
    notes: labServiceNote,
  },
  primDilutedDnaQuality230: {
    header: 'Diluted DNA\nquality 260/230',
    tooltip: 'Measured with the NanoDrop One spectrophotometer.',
    acceptedRange: nanoDrop230Range,
    showRangeHint: false,
    softValidationEnabled: false,
    notes: labServiceNote,
  },
  primDilutedDnaVolume: {
    header: 'Diluted DNA volume\n(\u00b5L)',
    tooltip: 'Minimum diluted DNA volume depends on the selected panel/layout.',
    notes: 'Hidden if DNA extraction or dilution service is included (future feature).',
  },
  primDnaExtractionKit: {
    header: 'DNA extraction kit',
    tooltip: 'Kit or method used to extract sample DNA.',
    notes: 'Required when extraction service is not ordered (future validation).',
  },
  sampleCollectionDate: {
    header: 'Sample collection date',
    tooltip: 'Mandatory format: YYYY-MM-DD (e.g., 2024-03-15).',
  },
  city: {
    header: 'City\n\u00a0',
    notes: samplingPointNote,
  },
  stateOrRegion: {
    header: 'State\nor region',
    notes: samplingPointNote,
  },
  country: {
    header: 'Country\n\u00a0',
    tooltip: 'Sampling country (stored as ISO alpha-3 code, e.g., FIN).',
    notes: samplingPointNote,
  },
  latitudeLongitude: {
    header: 'Latitude, longitude\n(eg. 60.170451, 24.939575)',
    tooltip:
      'Coordinates of the sampling point in decimal degrees. You can get the coordinates by zooming in and long-clicking on the terrain on Google Maps outside map markers.',
    notes: samplingPointNote,
  },
};

export function getSampleSheetColumnMeta(key: keyof SampleSpecRow): SampleSheetColumnMeta {
  return sampleSheetColumnMetadata[key];
}

export function hasAcceptedRange(fieldKey: keyof SampleSpecRow): boolean {
  return Boolean(sampleSheetColumnMetadata[fieldKey].acceptedRange);
}
